// utils/branchNameResolver.js
// Utility to determine staff branch name and check if branch name is configured

import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * Get or check branch name for a staff member
 * - If staff has an explicit branch name (driveBranchName, address, or location), use it
 * - If not, returns null to indicate branch name needs to be configured
 * 
 * @param {Object} user - The Firebase user object
 * @param {string} adminUid - The admin's UID (for staff members)
 * @param {boolean} isAdmin - Whether the user is an admin
 * @returns {Promise<string|null>} The branch name to use for Drive folders, or null if not configured
 */
export async function resolveBranchName(user, adminUid, isAdmin) {
    // Admins always use 'adminUploads'
    if (isAdmin) {
        return 'adminUploads';
    }

    if (!user?.uid) {
        return null;
    }

    try {
        // Try to get existing branch name from agent-details
        const agentDoc = await getDoc(doc(db, 'agent-details', user.uid));

        if (agentDoc.exists()) {
            const agentData = agentDoc.data();

            // Check for existing branch name (explicit or assigned)
            const branchName = agentData.driveBranchName || agentData.address || agentData.location;

            if (branchName && branchName.trim()) {
                return branchName.trim();
            }
        }

        // No branch name configured - return null
        return null;

    } catch (error) {
        console.error('Error resolving branch name:', error);
        return null;
    }
}

/**
 * Check if a staff member has a branch name configured
 * 
 * @param {string} staffUid - The staff member's UID
 * @returns {Promise<boolean>} True if branch name is configured
 */
export async function hasBranchName(staffUid) {
    if (!staffUid) return true; // Assume admins don't need this check

    try {
        const agentDoc = await getDoc(doc(db, 'agent-details', staffUid));

        if (agentDoc.exists()) {
            const data = agentDoc.data();
            const branchName = data.driveBranchName || data.address || data.location;
            return !!(branchName && branchName.trim());
        }

        return false;
    } catch (error) {
        console.error('Error checking branch name:', error);
        return false;
    }
}

/**
 * Update the Drive branch name for a staff member
 * This should be called when the staff's profile (address/location) is updated
 * 
 * @param {string} staffUid - The staff member's UID
 * @param {string} newBranchName - The new branch name to use
 * @returns {Promise<void>}
 */
export async function updateDriveBranchName(staffUid, newBranchName) {
    if (!staffUid || !newBranchName || !newBranchName.trim()) {
        return;
    }

    try {
        const agentRef = doc(db, 'agent-details', staffUid);
        await setDoc(agentRef, {
            driveBranchName: newBranchName.trim(),
            updatedAt: new Date().toISOString()
        }, { merge: true });

        console.log(`📂 Updated Drive branch name to: ${newBranchName} for staff ${staffUid}`);
    } catch (error) {
        console.error('Error updating Drive branch name:', error);
    }
}

/**
 * Get the current Drive branch name for a staff member
 * 
 * @param {string} staffUid - The staff member's UID
 * @returns {Promise<string|null>} The current branch name or null if not set
 */
export async function getCurrentDriveBranchName(staffUid) {
    if (!staffUid) return null;

    try {
        const agentDoc = await getDoc(doc(db, 'agent-details', staffUid));

        if (agentDoc.exists()) {
            const data = agentDoc.data();
            return data.driveBranchName || data.address || data.location || null;
        }

        return null;
    } catch (error) {
        console.error('Error getting current Drive branch name:', error);
        return null;
    }
}
